import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import AdminPage from "./AdminPage";
import BrandLogo from "./components/BrandLogo";
import { supabase } from "./lib/supabase";
import { copySticker, DEFAULT_PLAN_FEATURES, downloadSticker, hasPlanFeature, imageProxyUrl, normalizePlanFeatures, originalImageUrl, subscriptionState, thumbnailUrl } from "./lib/stickers";

const CHECKOUTS = {
  mensal: "https://packfigure.site/#oferta",
  trimestral: "https://checkout.wiven.com.br/checkout/cmv5qxelh2w3z01pss0zpr1mu?offer=VNKHM0I",
  semestral: "https://packfigure.site/#oferta",
  anual: "https://packfigure.site/#oferta",
};
const DEFAULT_CHECKOUT = "https://packfigure.site/#oferta";
const PREMIUM_CATEGORY_ID = "b3ea1425-a352-4ade-9f10-97fdd86d23d9";
const PREMIUM_CHECKOUT = "https://checkout.wiven.com.br/checkout/cmv5qxelh2w3z01pss0zpr1mu?offer=DVZCGXY";
const DEFAULT_SITE_SETTINGS = {
  brand_name: "PackFigure",
  tagline: "Figurinhas que expressam você",
  accent: "#FF2878",
  logo_url: "",
  banner_title: "Criatividade em cada Story.",
  banner_subtitle: "Mais expressão para deixar seus conteúdos ainda mais especiais.",
  support_email: "nflrailan1@gmail.com",
  support_whatsapp: "5577998295988",
  checkout_default: DEFAULT_CHECKOUT,
  checkout_mensal: CHECKOUTS.mensal,
  checkout_trimestral: CHECKOUTS.trimestral,
  checkout_semestral: CHECKOUTS.semestral,
  checkout_anual: CHECKOUTS.anual,
  checkout_vitalicio: PREMIUM_CHECKOUT,
  wiven_offer_trimestral: "",
  wiven_offer_vitalicio: "",
  plan_features: DEFAULT_PLAN_FEATURES,
};

function normalizeSiteSettings(data = {}) {
  const next = { ...DEFAULT_SITE_SETTINGS, ...data };
  next.plan_features = normalizePlanFeatures(data.plan_features);
  next.support_email = DEFAULT_SITE_SETTINGS.support_email;
  next.support_whatsapp = DEFAULT_SITE_SETTINGS.support_whatsapp;
  next.checkout_default = DEFAULT_SITE_SETTINGS.checkout_default;
  next.checkout_mensal = DEFAULT_SITE_SETTINGS.checkout_mensal;
  next.checkout_trimestral = DEFAULT_SITE_SETTINGS.checkout_trimestral;
  next.checkout_semestral = DEFAULT_SITE_SETTINGS.checkout_semestral;
  next.checkout_anual = DEFAULT_SITE_SETTINGS.checkout_anual;
  next.checkout_vitalicio = DEFAULT_SITE_SETTINGS.checkout_vitalicio;
  if (String(data.brand_name || "").trim().toLowerCase() !== "packfigure") next.brand_name = DEFAULT_SITE_SETTINGS.brand_name;
  if (!data.tagline || ["um álbum inteiro no seu bolso", "Figurinhas para Stories"].includes(data.tagline)) next.tagline = DEFAULT_SITE_SETTINGS.tagline;
  if (["#ff3d7f", "#ef4c83"].includes(String(data.accent || "").toLowerCase())) next.accent = DEFAULT_SITE_SETTINGS.accent;
  if (!data.banner_title || data.banner_title === "Escolha, copie e deixe seu Story com a sua cara.") next.banner_title = DEFAULT_SITE_SETTINGS.banner_title;
  if (!data.banner_subtitle || data.banner_subtitle === "Novas figurinhas para transformar seus conteúdos.") next.banner_subtitle = DEFAULT_SITE_SETTINGS.banner_subtitle;
  return next;
}

const LEGAL_CONTENT = {
  termos: {
    title: "Termos de Uso",
    updated: "15 de outubro de 2026",
    intro: "Estes Termos de Uso regulam o acesso e a utilização da plataforma digital PackFigure. Ao criar uma conta ou usar o serviço, você concorda com estas condições.",
    sections: [
      ["1. O que é a PackFigure", ["A PackFigure é um aplicativo (PWA) que disponibiliza um acervo de figurinhas digitais em formato PNG para uso em Stories do Instagram, WhatsApp e outras redes sociais. O acesso ao acervo é concedido mediante assinatura paga, com prazo de validade conforme o plano contratado."]],
      ["2. Cadastro e acesso", ["Para usar o serviço, você precisa de uma conta criada após a confirmação do pagamento.", "Você receberá no WhatsApp informado na compra o endereço de acesso, seu e-mail e uma senha temporária.", "No primeiro acesso, você deverá criar uma nova senha pessoal.", "Você é responsável por manter a confidencialidade da sua senha e por todas as atividades realizadas na sua conta.", "O acesso é pessoal e intransferível. É vedado o compartilhamento de login com terceiros."]],
      ["3. Planos e pagamento", ["Oferecemos planos com diferentes durações de acesso, conforme as opções exibidas no momento da compra.", "O pagamento é processado pela plataforma Wiven, com os meios exibidos no checkout.", "A existência de cobrança recorrente ou pagamento único deve ser informada claramente na oferta e no checkout. Ao fim do período contratado, o acesso pode expirar conforme o plano escolhido.", "Avisaremos por e-mail e no app quando sua assinatura estiver próxima do vencimento."]],
      ["4. Direito de arrependimento e reembolso", ["Você pode solicitar o cancelamento em até 7 (sete) dias corridos a contar da confirmação do pagamento. Para solicitar, entre em contato pelo e-mail nflrailan1@gmail.com ou pelo WhatsApp (77) 99829-5988. Após o reembolso, o acesso ao acervo é desativado."]],
      ["5. Licença de uso das figurinhas", ["Durante a vigência da assinatura, você recebe uma licença não exclusiva e intransferível para usar as figurinhas em suas publicações pessoais e comerciais nas redes sociais.", "É proibido revender, redistribuir, compartilhar os arquivos com terceiros, disponibilizar o acervo em outros aplicativos ou sites, ou reivindicar autoria sobre as figurinhas.", "O acervo e a marca PackFigure são protegidos por direitos autorais e propriedade intelectual."]],
      ["6. Uso adequado", ["Você concorda em não utilizar o serviço para fins ilícitos ou que violem direitos de terceiros; tentar acessar áreas restritas, copiar o acervo em massa ou burlar mecanismos de segurança; ou compartilhar suas credenciais de acesso.", "O descumprimento pode resultar na suspensão ou cancelamento da conta, sem direito a reembolso proporcional, nos casos de violação grave."]],
      ["7. Disponibilidade do serviço", ["Nos esforçamos para manter o serviço sempre disponível, mas ele pode passar por manutenções ou interrupções temporárias. Novas figurinhas podem ser adicionadas e, eventualmente, itens podem ser removidos do acervo. Não garantimos a permanência de figurinhas específicas."]],
      ["8. Alterações nos termos", ["Podemos atualizar estes Termos a qualquer momento. Mudanças relevantes serão comunicadas por e-mail ou no app. O uso continuado após as alterações representa concordância com a nova versão."]],
      ["9. Contato", ["Dúvidas sobre estes Termos? Fale com a PackFigure pelo e-mail nflrailan1@gmail.com ou pelo WhatsApp (77) 99829-5988."]],
      ["10. Legislação aplicável", ["Estes Termos são regidos pelas leis brasileiras, respeitados os direitos do consumidor e as regras de competência previstas na legislação aplicável."]],
    ],
  },
  privacidade: {
    title: "Política de Privacidade",
    updated: "15 de outubro de 2026",
    intro: "Esta Política de Privacidade explica como a PackFigure trata e protege dados pessoais relacionados ao uso do aplicativo e do site, em conformidade com a legislação brasileira aplicável.",
    sections: [
      ["1. Quais dados coletamos", ["Nome e e-mail, informados na compra e usados para criar e identificar sua conta.", "Senha, armazenada de forma criptografada. Nunca temos acesso à senha em texto.", "Informações sobre o pedido, como plano, valor, data e identificador da compra, recebidas da plataforma de pagamento. Não armazenamos dados de cartão de crédito.", "Figurinhas favoritadas e dados agregados de navegação e desempenho para melhorar o aplicativo."]],
      ["2. Para que usamos seus dados", ["Criar e gerenciar sua conta e seu acesso ao acervo; processar pagamentos e validar a assinatura; enviar e-mails essenciais; enviar notificações quando autorizadas; prestar suporte; melhorar o serviço e prevenir fraudes."]],
      ["3. Base legal", ["Tratamos seus dados com base na execução do contrato, no cumprimento de obrigação legal, no legítimo interesse relacionado à segurança e melhoria do serviço e no seu consentimento para notificações opcionais."]],
      ["4. Com quem compartilhamos", ["Não vendemos seus dados. Compartilhamos apenas com prestadores essenciais: Wiven para pagamentos; Supabase para autenticação e banco de dados; Cloudflare R2 para armazenamento de imagens; Cloudflare Pages para hospedagem; e o serviço de e-mail transacional contratado.", "Esses serviços podem processar dados em servidores fora do Brasil. Adotamos medidas para garantir proteção adequada, conforme a LGPD."]],
      ["5. Por quanto tempo guardamos", ["Mantemos seus dados enquanto sua conta existir e pelo prazo necessário para cumprir obrigações legais. Você pode solicitar a exclusão a qualquer momento, ressalvadas as informações que precisamos reter por lei."]],
      ["6. Seus direitos", ["Você pode confirmar a existência de tratamento; acessar e corrigir seus dados; solicitar anonimização ou eliminação quando aplicável; revogar consentimentos; e solicitar portabilidade. Para exercer esses direitos, envie um e-mail para nflrailan1@gmail.com ou fale pelo WhatsApp (77) 99829-5988."]],
      ["7. Segurança", ["Adotamos medidas técnicas e organizacionais para proteger seus dados, incluindo criptografia de senhas, conexão HTTPS e controle de acesso. Nenhum sistema é totalmente infalível, mas trabalhamos continuamente para manter seus dados protegidos."]],
      ["8. Notificações push", ["As notificações são opcionais e só são enviadas após autorização no dispositivo. Você pode desativá-las a qualquer momento nas configurações do aplicativo ou navegador."]],
      ["9. Crianças e adolescentes", ["O serviço é destinado a maiores de 18 anos. Não coletamos intencionalmente dados de menores sem o consentimento dos responsáveis."]],
      ["10. Alterações nesta política", ["Podemos atualizar esta Política periodicamente. Mudanças relevantes serão comunicadas por e-mail ou no aplicativo."]],
      ["11. Contato", ["Para questões sobre privacidade e proteção de dados, fale com a PackFigure pelo e-mail nflrailan1@gmail.com ou pelo WhatsApp (77) 99829-5988."]],
    ],
  },
};

const AuthContext = createContext(null);
const SiteSettingsContext = createContext(DEFAULT_SITE_SETTINGS);

function useSiteSettings() {
  return useContext(SiteSettingsContext);
}

function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SITE_SETTINGS);
  useEffect(() => {
    let live = true;
    fetch("/api/settings")
      .then((response) => response.ok ? response.json() : {})
      .then((data) => { if (live) setSettings(normalizeSiteSettings(data)); })
      .catch(() => {});
    return () => { live = false; };
  }, []);
  useEffect(() => {
    document.title = settings.brand_name || "PackFigure";
    document.documentElement.style.setProperty("--petal", settings.accent || DEFAULT_SITE_SETTINGS.accent);
  }, [settings]);
  return <SiteSettingsContext.Provider value={settings}>{children}</SiteSettingsContext.Provider>;
}

function checkoutFor(settings, plan, premium = false) {
  if (premium) return settings.checkout_vitalicio || PREMIUM_CHECKOUT;
  return settings[`checkout_${plan || ""}`] || settings.checkout_default || CHECKOUTS[plan] || DEFAULT_CHECKOUT;
}

function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (user) => {
    if (!user) {
      setProfile(null);
      return;
    }
    const { data } = await supabase.from("usuarios").select("*").eq("id", user.id).maybeSingle();
    setProfile(data || null);
  }, []);

  useEffect(() => {
    let live = true;
    supabase.auth.getSession().then(async ({ data }) => {
      if (!live) return;
      setSession(data.session || null);
      await loadProfile(data.session?.user);
      if (live) setLoading(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
      setSession(nextSession);
      await loadProfile(nextSession?.user);
      setLoading(false);
    });
    return () => {
      live = false;
      listener.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const value = useMemo(
    () => ({ session, user: session?.user || null, profile, setProfile, loading, refreshProfile: () => loadProfile(session?.user) }),
    [session, profile, loading, loadProfile],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function Protected({ children }) {
  const { user, profile, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (profile?.deve_trocar_senha && pathname !== "/perfil/trocar-senha") return <Navigate to="/perfil/trocar-senha" replace />;
  return children;
}

function FeatureGate({ feature, children }) {
  const { profile, loading } = useAuth();
  const settings = useSiteSettings();
  if (loading) return <Splash />;
  return hasPlanFeature(profile, settings, feature) ? children : <Navigate to="/" replace />;
}

function AdminGate() {
  const { profile, loading } = useAuth();
  if (loading) return <Splash />;
  return <AdminPage profile={profile} />;
}

function Splash() {
  return (
    <main className="splash" aria-live="polite">
      <div className="brand-mark"><img src="/packfigure-icon.svg" alt="" /></div>
      <span>Preparando suas figurinhas…</span>
    </main>
  );
}

function AppShell({ children }) {
  return (
    <div className="app-frame">
      <div className="app-content">{children}</div>
      <BottomNav />
    </div>
  );
}

function BottomNav() {
  const { pathname } = useLocation();
  const { profile } = useAuth();
  const settings = useSiteSettings();
  const items = [
    ["/", "⌂", "Início"],
    hasPlanFeature(profile, settings, "acervo") && ["/temas", "✦", "Temas"],
    hasPlanFeature(profile, settings, "favoritos") && ["/favoritos", "♥", "Favoritos"],
    ["/perfil", "●", "Perfil"],
  ].filter(Boolean);
  return (
    <nav className="bottom-nav" aria-label="Navegação principal" style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}>
      {items.map(([href, icon, label]) => {
        const active = pathname === href || (href !== "/" && pathname.startsWith(href));
        return (
          <Link key={href} to={href} className={active ? "active" : ""} aria-current={active ? "page" : undefined}>
            <span className="nav-icon">{icon}</span>
            <span>{label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

function PageHeader({ title, eyebrow, action, back }) {
  const navigate = useNavigate();
  return (
    <header className="page-header">
      <div className={`header-copy${back ? " has-back" : ""}`}>
        {back && (
          <button className="back-button" onClick={() => navigate(-1)} aria-label="Voltar">
            ←
          </button>
        )}
        <div>
          {eyebrow && <p className="eyebrow">{eyebrow}</p>}
          <h1>{title}</h1>
        </div>
      </div>
      {action}
    </header>
  );
}

function LoginPage() {
  const { user, loading } = useAuth();
  const settings = useSiteSettings();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  if (!loading && user) return <Navigate to="/" replace />;

  async function handleSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    if (mode === "login") {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      setBusy(false);
      if (error) return setMessage("E-mail ou senha incorretos. Confira seus dados e tente novamente.");
      navigate("/");
      return;
    }
    if (mode === "request") {
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      setBusy(false);
      if (error) return setMessage("Não foi possível enviar o código. Confira o e-mail.");
      setMode("verify");
      return;
    }
    if (code.replace(/\D/g, "").length < 6) {
      setBusy(false);
      return setMessage("Digite o código completo recebido por e-mail.");
    }
    if (newPassword.length < 6 || newPassword !== confirmPassword) {
      setBusy(false);
      return setMessage("As senhas precisam ter ao menos 6 caracteres e serem iguais.");
    }
    const { error: otpError } = await supabase.auth.verifyOtp({ email, token: code.replace(/\D/g, ""), type: "recovery" });
    if (otpError) {
      setBusy(false);
      return setMessage("Código inválido ou expirado.");
    }
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setBusy(false);
    if (error) return setMessage("Não foi possível salvar a nova senha.");
    navigate("/");
  }

  return (
    <main className="login-page">
      <section className="login-visual" aria-hidden="true">
        <div className="sticker-orbit orbit-one">bom dia!</div>
        <div className="sticker-orbit orbit-two">feito com ♥</div>
        <div className="sticker-orbit orbit-three">novidade</div>
        <div className="hero-brand">
          <BrandLogo name={settings.brand_name} tagline={settings.tagline} logoUrl={settings.logo_url} showTagline className="hero-lockup" />
        </div>
      </section>
      <section className="login-card">
        <p className="eyebrow">Acesso à biblioteca</p>
        <h1>{mode === "verify" ? "Digite o código" : mode === "request" ? "Recupere sua senha" : "Que bom ter você aqui"}</h1>
        <p className="muted">
          {mode === "verify" ? `Enviamos um código para ${email}.` : "Entre com os mesmos dados da PackFigure."}
        </p>
        <form onSubmit={handleSubmit}>
          {mode !== "verify" && (
            <label>
              E-mail
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
            </label>
          )}
          {mode === "login" && (
            <label>
              Senha
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
            </label>
          )}
          {mode === "verify" && (
            <>
              <label>
                Código do e-mail
                <input className="code-input" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 10))} required />
              </label>
              <label>
                Nova senha
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
              </label>
              <label>
                Confirme a senha
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
              </label>
            </>
          )}
          {message && <p className="form-error">{message}</p>}
          <button className="primary-button" disabled={busy} type="submit">
            {busy ? "Aguarde…" : mode === "login" ? "Entrar" : mode === "request" ? "Enviar código" : "Salvar nova senha"}
          </button>
        </form>
        <button className="text-button" onClick={() => { setMessage(""); setMode(mode === "login" ? "request" : "login"); }}>
          {mode === "login" ? "Esqueci minha senha →" : "← Voltar para o login"}
        </button>
        <p className="microcopy">Primeira vez? Use o convite enviado ao seu e-mail para definir a senha.</p>
      </section>
    </main>
  );
}

function useCatalog() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    supabase
      .from("categorias")
      .select("*, temas(id, nome, emoji, total_figurinhas)")
      .order("ordem")
      .then(({ data, error: queryError }) => {
        if (!live) return;
        setCategories((data || []).filter((category) => category.id !== PREMIUM_CATEGORY_ID));
        setError(queryError?.message || "");
        setLoading(false);
      });
    return () => { live = false; };
  }, []);
  return { categories, loading, error };
}

function useFeatured(limit = 9) {
  const [stickers, setStickers] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    supabase
      .from("figurinhas")
      .select("*, temas!inner(categoria_id)")
      .eq("ativo", true)
      .eq("destaque", true)
      .neq("temas.categoria_id", PREMIUM_CATEGORY_ID)
      .order("criado_em", { ascending: false })
      .limit(limit)
      .then(async ({ data }) => {
        let result = data || [];
        if (!result.length) {
          const fallback = await supabase.from("figurinhas").select("*, temas!inner(categoria_id)").eq("ativo", true).neq("temas.categoria_id", PREMIUM_CATEGORY_ID).order("criado_em", { ascending: false }).limit(limit);
          result = fallback.data || [];
        }
        if (live) {
          setStickers(result);
          setLoading(false);
        }
      });
    return () => { live = false; };
  }, [limit]);
  return { stickers, loading };
}

function HomePage() {
  const { user, profile } = useAuth();
  const settings = useSiteSettings();
  const { categories, loading: catalogLoading } = useCatalog();
  const { stickers, loading: stickersLoading } = useFeatured(9);
  const firstName = (profile?.nome || user?.user_metadata?.nome || user?.email?.split("@")[0] || "você").split(" ")[0];
  const canUseCatalog = hasPlanFeature(profile, settings, "acervo");
  const canUsePremium = hasPlanFeature(profile, settings, "sombras_molduras");
  const canUseSuppliers = hasPlanFeature(profile, settings, "fornecedores");
  const canUseTutorials = hasPlanFeature(profile, settings, "tutoriais");
  return (
    <AppShell>
      <main className="page home-page">
        <header className="home-hero">
          <div>
            <p className="brand-word"><BrandLogo name={settings.brand_name} tagline={settings.tagline} logoUrl={settings.logo_url} /></p>
            <h1>Olá, {firstName} <span aria-hidden="true">✦</span></h1>
            <p>{settings.banner_title}</p>
          </div>
          <Link className="avatar" to="/perfil" aria-label="Abrir perfil">{firstName.charAt(0).toUpperCase()}</Link>
        </header>

        {canUseCatalog && <section className="section-block">
          <SectionTitle title="Comece por aqui" action={<Link to="/temas">Ver todas →</Link>} />
          <div className="category-ribbon">
            {catalogLoading ? <CardSkeleton count={4} /> : categories.slice(0, 7).map((category, index) => (
              <Link className={`category-chip tone-${index % 6}`} to={`/temas?categoria=${category.id}`} key={category.id}>
                <span className="category-emoji">{category.emoji}</span>
                <strong>{category.nome}</strong>
                <small>{category.temas?.length || 0} temas</small>
              </Link>
            ))}
          </div>
        </section>}

        {canUseCatalog && <section className="section-block">
          <SectionTitle title="Escolhas de hoje" subtitle={settings.banner_subtitle} />
          <StickerGrid stickers={stickers} loading={stickersLoading} compact />
        </section>}

        {(canUsePremium || canUseSuppliers) && <section className="section-block">
          <SectionTitle title="Coleções especiais" subtitle="Materiais extras para criar Stories completos" />
          <div className="product-grid">
            {canUsePremium && <Link className="product-card product-shadows" to="/premium">
              <span className="product-badge">2026</span>
              <div><small>Kit visual</small><strong>Sombras<br />& Molduras</strong></div>
              <b>Abrir →</b>
            </Link>}
            {canUseSuppliers && <div className="product-card product-suppliers" aria-label="Lista de fornecedores">
              <span className="product-badge">NOVO</span>
              <div><small>Diretório curado</small><strong>Lista de<br />Fornecedores</strong></div>
              <b><a href="https://packfigure.site/">Abrir →</a></b>
            </div>}
          </div>
        </section>}

        {canUseTutorials && <section className="feature-card">
          <div className="feature-icon">▶</div>
          <div>
            <p className="eyebrow">Aprenda em minutos</p>
            <h2>Copiou. Colou. Publicou.</h2>
            <p>Veja como usar as figurinhas no Instagram e no WhatsApp.</p>
          </div>
          <Link to="/tutoriais" aria-label="Abrir tutoriais">→</Link>
        </section>}
      </main>
    </AppShell>
  );
}

function SectionTitle({ title, subtitle, action }) {
  return (
    <div className="section-title">
      <div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
      {action}
    </div>
  );
}

function ThemesPage() {
  const { categories, loading, error } = useCatalog();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const requested = new URLSearchParams(location.search).get("categoria");
  const [openId, setOpenId] = useState(requested || null);

  useEffect(() => { if (requested) setOpenId(requested); }, [requested]);

  const filtered = useMemo(() => {
    const term = query.trim().toLocaleLowerCase("pt-BR");
    if (!term) return categories;
    return categories
      .map((category) => ({ ...category, temas: (category.temas || []).filter((theme) => theme.nome.toLocaleLowerCase("pt-BR").includes(term)) }))
      .filter((category) => category.nome.toLocaleLowerCase("pt-BR").includes(term) || category.temas.length);
  }, [categories, query]);

  return (
    <AppShell>
      <main className="page themes-page">
        <PageHeader title="Temas" eyebrow="Seu acervo completo" />
        <label className="search-field">
          <span>⌕</span>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Busque por café, bom dia, promoção…" />
          {query && <button onClick={() => setQuery("")} aria-label="Limpar busca">×</button>}
        </label>
        {error && <EmptyState icon="!" title="Não foi possível carregar" text="Atualize a página e tente novamente." />}
        {loading ? <ListSkeleton /> : (
          <div className="accordion-list">
            {filtered.map((category, index) => {
              const open = openId === category.id || Boolean(query);
              const total = (category.temas || []).reduce((sum, theme) => sum + (theme.total_figurinhas || 0), 0);
              return (
                <section className={`category-panel tone-${index % 6}`} key={category.id}>
                  <button className="category-heading" onClick={() => setOpenId(open ? null : category.id)} aria-expanded={open}>
                    <span className="category-emoji">{category.emoji}</span>
                    <span><strong>{category.nome}</strong><small>{category.temas?.length || 0} temas · {total.toLocaleString("pt-BR")} figurinhas</small></span>
                    <b>{open ? "−" : "+"}</b>
                  </button>
                  {open && (
                    <div className="theme-links">
                      {(category.temas || []).map((theme) => (
                        <Link to={`/galeria/${theme.id}`} key={theme.id}>
                          <span>{theme.emoji || "✦"}</span>
                          <strong>{theme.nome}</strong>
                          <small>{(theme.total_figurinhas || 0).toLocaleString("pt-BR")} figurinhas</small>
                          <b>→</b>
                        </Link>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
            {!filtered.length && <EmptyState icon="⌕" title="Nada por aqui" text="Tente buscar com outra palavra." />}
          </div>
        )}
      </main>
    </AppShell>
  );
}

function useFavorites(userId) {
  const [ids, setIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!userId) return;
    let live = true;
    supabase.from("favoritos").select("figurinha_id").eq("usuario_id", userId).then(({ data }) => {
      if (live) {
        setIds(new Set((data || []).map((row) => row.figurinha_id)));
        setLoading(false);
      }
    });
    return () => { live = false; };
  }, [userId]);

  const toggle = useCallback(async (id) => {
    const exists = ids.has(id);
    setIds((current) => {
      const next = new Set(current);
      exists ? next.delete(id) : next.add(id);
      return next;
    });
    if (exists) {
      await supabase.from("favoritos").delete().eq("usuario_id", userId).eq("figurinha_id", id);
    } else {
      await supabase.from("favoritos").insert({ usuario_id: userId, figurinha_id: id });
    }
  }, [ids, userId]);
  return { ids, toggle, loading };
}

function GalleryPage() {
  const { themeId } = useParams();
  const { user, profile } = useAuth();
  const [theme, setTheme] = useState(null);
  const [stickers, setStickers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState("");
  const { ids, toggle } = useFavorites(user?.id);
  const subscription = subscriptionState(profile);

  useEffect(() => {
    let live = true;
    Promise.all([
      supabase.from("temas").select("id,nome,emoji,total_figurinhas").eq("id", themeId).maybeSingle(),
      supabase.from("figurinhas").select("*").eq("tema_id", themeId).eq("ativo", true).order("ordem", { ascending: true }),
    ]).then(([themeResult, stickerResult]) => {
      if (!live) return;
      setTheme(themeResult.data || null);
      setStickers(stickerResult.data || []);
      setLoading(false);
    });
    return () => { live = false; };
  }, [themeId]);

  function openSticker(sticker) {
    if (subscription.blocked) {
      setToast("Sua assinatura precisa ser renovada para copiar figurinhas.");
      setTimeout(() => setToast(""), 3000);
      return;
    }
    setSelected(sticker);
  }

  return (
    <AppShell>
      <main className="page gallery-page">
        <PageHeader back title={theme?.nome || "Figurinhas"} eyebrow={`${stickers.length || theme?.total_figurinhas || 0} opções`} />
        <SubscriptionBanner profile={profile} />
        <p className="gallery-tip"><span>Toque para abrir</span><span>♥ para favoritar</span></p>
        <StickerGrid stickers={stickers} loading={loading} favorites={ids} onFavorite={toggle} onOpen={openSticker} />
        {selected && <StickerModal sticker={selected} onClose={() => setSelected(null)} onToast={(value) => { setToast(value); setTimeout(() => setToast(""), 2800); }} />}
        <Toast message={toast} />
      </main>
    </AppShell>
  );
}

function PremiumPage() {
  const { user, profile } = useAuth();
  const settings = useSiteSettings();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState("");
  const { ids, toggle } = useFavorites(user?.id);
  const unlocked = hasPlanFeature(profile, settings, "sombras_molduras");

  useEffect(() => {
    if (!unlocked) {
      setLoading(false);
      return;
    }
    let live = true;
    supabase.from("temas").select("id,nome,emoji").eq("categoria_id", PREMIUM_CATEGORY_ID).then(async ({ data }) => {
      const result = await Promise.all((data || []).map(async (theme) => {
        const response = await supabase.from("figurinhas").select("*").eq("tema_id", theme.id).eq("ativo", true).order("ordem", { ascending: true });
        return { ...theme, stickers: response.data || [] };
      }));
      if (live) {
        setGroups(result);
        setLoading(false);
      }
    });
    return () => { live = false; };
  }, [unlocked]);

  if (!unlocked && !loading) {
    return <Navigate to="/" replace />;
  }
  return (
    <AppShell>
      <main className="page premium-page">
        <PageHeader back title="Sombras & Molduras" eyebrow="Pack Premium · toque para usar" />
        {loading ? <StickerGrid loading stickers={[]} /> : groups.map((group) => (
          <section className="premium-group" key={group.id}>
            <SectionTitle title={`${group.emoji || "✦"} ${group.nome}`} subtitle={`${group.stickers.length} itens`} />
            <StickerGrid stickers={group.stickers} favorites={ids} onFavorite={toggle} onOpen={setSelected} />
          </section>
        ))}
        {selected && <StickerModal sticker={selected} onClose={() => setSelected(null)} onToast={setToast} />}
        <Toast message={toast} />
      </main>
    </AppShell>
  );
}

function StickerGrid({ stickers = [], loading, favorites = new Set(), onFavorite, onOpen, compact = false }) {
  const [preview, setPreview] = useState(null);
  const opener = onOpen || setPreview;
  if (loading) return <div className={`sticker-grid ${compact ? "compact" : ""}`}><CardSkeleton count={compact ? 6 : 15} /></div>;
  return (
    <>
      <div className={`sticker-grid ${compact ? "compact" : ""}`}>
        {stickers.map((sticker) => (
          <StickerCard key={sticker.id} sticker={sticker} favorite={favorites.has(sticker.id)} onFavorite={onFavorite} onOpen={opener} />
        ))}
      </div>
      {!stickers.length && <EmptyState icon="✦" title="Nenhuma figurinha encontrada" text="Volte em breve para ver as novidades." />}
      {preview && <StickerModal sticker={preview} onClose={() => setPreview(null)} />}
    </>
  );
}

function StickerCard({ sticker, favorite, onFavorite, onOpen }) {
  const [source, setSource] = useState(thumbnailUrl(sticker.url));
  useEffect(() => setSource(thumbnailUrl(sticker.url)), [sticker.url]);
  return (
    <article className="sticker-card">
      <button className="sticker-open" onClick={() => onOpen(sticker)} aria-label="Abrir figurinha">
        <img loading="lazy" decoding="async" src={source} onError={() => setSource(sticker.url)} alt="Figurinha" />
        <span className="peek">⌕</span>
      </button>
      {onFavorite && (
        <button className={`favorite-button ${favorite ? "saved" : ""}`} onClick={() => onFavorite(sticker.id)} aria-label={favorite ? "Remover favorito" : "Favoritar"}>
          ♥
        </button>
      )}
    </article>
  );
}

function StickerModal({ sticker, onClose, onToast }) {
  const [busy, setBusy] = useState("");
  const [displayUrl, setDisplayUrl] = useState(originalImageUrl(sticker.url));

  useEffect(() => {
    let objectUrl;
    let live = true;
    const original = originalImageUrl(sticker.url);
    fetch(imageProxyUrl(original))
      .then((response) => response.ok ? response.blob() : Promise.reject())
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (live) setDisplayUrl(objectUrl);
      })
      .catch(() => setDisplayUrl(sticker.url));
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [sticker.url]);

  async function handleCopy() {
    setBusy("copy");
    const ok = await copySticker(sticker.url);
    setBusy("");
    onToast?.(ok ? "Figurinha copiada! Agora é só colar no Story ✦" : "Não foi possível copiar. Use o botão de baixar.");
    if (ok) setTimeout(onClose, 650);
  }

  async function handleDownload() {
    setBusy("download");
    const ok = await downloadSticker(sticker.url);
    setBusy("");
    onToast?.(ok ? "Figurinha baixada com sucesso." : "Não foi possível baixar esta figurinha.");
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sticker-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Fechar">×</button>
        <div className="modal-image"><img src={displayUrl} alt="Figurinha ampliada" /></div>
        <div className="modal-actions">
          <button className="primary-button" onClick={handleCopy} disabled={Boolean(busy)}>{busy === "copy" ? "Copiando…" : "Copiar imagem"}</button>
          <button className="secondary-button" onClick={handleDownload} disabled={Boolean(busy)}>{busy === "download" ? "Baixando…" : "Baixar PNG"}</button>
        </div>
        <p>Copie e cole por cima da sua foto no Story. No Android, baixar pode funcionar melhor.</p>
      </div>
    </div>
  );
}

function FavoritesPage() {
  const { user, profile } = useAuth();
  const { ids, toggle, loading: favoritesLoading } = useFavorites(user?.id);
  const [stickers, setStickers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState("");
  const subscription = subscriptionState(profile);

  useEffect(() => {
    if (favoritesLoading) return;
    if (!ids.size) {
      setStickers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    supabase.from("figurinhas").select("*").in("id", Array.from(ids)).eq("ativo", true).then(({ data }) => {
      setStickers(data || []);
      setLoading(false);
    });
  }, [ids, favoritesLoading]);

  return (
    <AppShell>
      <main className="page">
        <PageHeader title="Favoritos" eyebrow={`${stickers.length} ${stickers.length === 1 ? "figurinha salva" : "figurinhas salvas"}`} />
        <SubscriptionBanner profile={profile} />
        {!loading && !stickers.length ? (
          <EmptyState icon="♥" title="Seu álbum está vazio" text="Toque no coração das figurinhas que você mais usa." action={<Link className="primary-button inline" to="/temas">Explorar temas</Link>} />
        ) : <StickerGrid stickers={stickers} loading={loading} favorites={ids} onFavorite={toggle} onOpen={(sticker) => subscription.blocked ? setToast("Renove sua assinatura para copiar figurinhas.") : setSelected(sticker)} />}
        {selected && <StickerModal sticker={selected} onClose={() => setSelected(null)} onToast={setToast} />}
        <Toast message={toast} />
      </main>
    </AppShell>
  );
}

function SubscriptionBanner({ profile }) {
  const settings = useSiteSettings();
  const state = subscriptionState(profile);
  if (state.status === "unknown" || state.status === "lifetime") return null;
  const checkout = checkoutFor(settings, profile?.plano);
  return (
    <aside className={`subscription-banner ${state.blocked ? "danger" : "warning"}`}>
      <span>{state.blocked ? "◆" : "◷"}</span>
      <div><strong>{state.blocked ? "Sua assinatura expirou" : `Sua assinatura vence em ${state.days} dias`}</strong><p>{state.blocked ? "Renove para voltar a copiar as figurinhas." : "Renove antes para continuar com acesso total."}</p></div>
      <a href={checkout} target="_blank" rel="noopener noreferrer">Renovar</a>
    </aside>
  );
}

function ProfilePage() {
  const { user, profile } = useAuth();
  const settings = useSiteSettings();
  const navigate = useNavigate();
  const status = subscriptionState(profile);
  const name = profile?.nome || user?.user_metadata?.nome || user?.email?.split("@")[0] || "Usuária";

  async function signOut() {
    await supabase.auth.signOut();
    navigate("/login");
  }

  return (
    <AppShell>
      <main className="page profile-page">
        <PageHeader title="Perfil" eyebrow="Conta e preferências" />
        <section className="profile-card">
          <div className="profile-avatar">{name.charAt(0).toUpperCase()}</div>
          <div><h2>{name}</h2><p>{user?.email}</p></div>
          <span className={`plan-pill ${status.status}`}>{status.status === "lifetime" ? "Vitalício" : status.status === "active" ? "Ativo" : "Renovar"}</span>
        </section>
        <section className="profile-list">
          <Link to="/tutoriais"><span>▶</span><div><strong>Tutoriais</strong><small>Aprenda a copiar, colar e instalar o app</small></div><b>→</b></Link>
          <Link to="/perfil/trocar-senha"><span>✦</span><div><strong>Trocar senha</strong><small>Atualize sua senha de acesso</small></div><b>→</b></Link>
          {profile?.is_admin && <Link to="/admin"><span>⚙</span><div><strong>Super Admin</strong><small>Gerencie usuários, conteúdo, marca e pagamentos</small></div><b>→</b></Link>}
          <a href={`mailto:${settings.support_email}`}><span>?</span><div><strong>Suporte</strong><small>Fale com a equipe {settings.brand_name}</small></div><b>→</b></a>
          <a href={`https://wa.me/${settings.support_whatsapp}?text=Oi%2C%20quero%20reportar%20um%20problema!`} target="_blank" rel="noopener noreferrer"><span>!</span><div><strong>Reportar problema</strong><small>Conte o que aconteceu</small></div><b>→</b></a>
          <button className="danger-row" onClick={signOut}><span>↗</span><div><strong>Sair</strong><small>Encerrar a sessão neste aparelho</small></div><b>→</b></button>
        </section>
      </main>
    </AppShell>
  );
}

function ChangePasswordPage() {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event) {
    event.preventDefault();
    if (password.length < 6 || password !== confirm) return setMessage("As senhas precisam ter ao menos 6 caracteres e serem iguais.");
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) { setBusy(false); return setMessage("Não foi possível atualizar a senha."); }
    const { error: confirmationError } = await supabase.rpc("confirmar_troca_senha");
    if (confirmationError) { setBusy(false); return setMessage("A senha foi alterada, mas não foi possível confirmar o primeiro acesso. Tente novamente."); }
    await refreshProfile();
    setBusy(false);
    navigate(profile?.deve_trocar_senha ? "/" : "/perfil");
  }
  return (
    <AppShell><main className="page narrow-page"><PageHeader back={!profile?.deve_trocar_senha} title={profile?.deve_trocar_senha ? "Crie sua nova senha" : "Trocar senha"} eyebrow={profile?.deve_trocar_senha ? "Primeiro acesso" : "Segurança da conta"} /><form className="form-card" onSubmit={submit}>{profile?.deve_trocar_senha && <p className="password-first-access">A senha recebida no WhatsApp é temporária. Escolha uma senha pessoal para continuar.</p>}<label>Nova senha<input type="password" minLength="6" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label><label>Confirme a nova senha<input type="password" minLength="6" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required /></label>{message && <p className="form-error">{message}</p>}<button className="primary-button" disabled={busy}>{busy ? "Salvando…" : "Salvar nova senha"}</button></form></main></AppShell>
  );
}

function TutorialsPage() {
  const [platform, setPlatform] = useState("android");
  const [open, setOpen] = useState(0);
  const tutorials = [
    ["📸", "Usar figurinhas no Story", "Como copiar e colar nos Stories do Instagram", "usar-figurinhas-story"],
    ["💬", "Usar figurinhas no WhatsApp", "Como enviar figurinhas pelo WhatsApp", "usar-figurinhas-whatsapp"],
    ["📲", "Instalar o app no celular", "Coloque a PackFigure como aplicativo na tela inicial", "instalar-app"],
  ];
  return (
    <AppShell><main className="page tutorials-page"><PageHeader back title="Tutoriais" eyebrow="Vídeos rápidos para você dominar" /><div className="platform-tabs"><button className={platform === "iphone" ? "active" : ""} onClick={() => { setPlatform("iphone"); setOpen(0); }}>iPhone</button><button className={platform === "android" ? "active" : ""} onClick={() => { setPlatform("android"); setOpen(0); }}>Android</button></div><div className="tutorial-list">{tutorials.map(([emoji, title, description, file], index) => <article key={file} className={open === index ? "open" : ""}><button onClick={() => setOpen(open === index ? -1 : index)}><span>{emoji}</span><div><strong>{title}</strong><small>{description}</small></div><b>{open === index ? "−" : "+"}</b></button>{open === index && <div className="video-wrap"><video controls playsInline preload="metadata" src={`https://pub-12d990c193174b56ad358762d4a29180.r2.dev/tutoriais/${file}-${platform}.mp4`} /></div>}</article>)}</div><aside className="tip-card"><span>💡</span><div><strong>Não achou a resposta?</strong><p>Abra Perfil → Reportar problema e mande sua dúvida.</p></div></aside></main></AppShell>
  );
}

function LegalPage({ type }) {
  const content = LEGAL_CONTENT[type];
  return (
    <main className="legal-page"><Link to="/login">← Voltar</Link><h1>{content.title}</h1><p className="legal-updated">Última atualização: {content.updated}</p><p>{content.intro}</p>{content.sections.map(([title, paragraphs]) => <section key={title}><h2>{title}</h2>{paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</section>)}</main>
  );
}

function CardSkeleton({ count = 6 }) {
  return Array.from({ length: count }).map((_, index) => <span className="card-skeleton" key={index} />);
}

function ListSkeleton() {
  return <div className="list-skeleton"><span /><span /><span /><span /><span /></div>;
}

function EmptyState({ icon, title, text, action }) {
  return <section className="empty-state"><span>{icon}</span><h2>{title}</h2><p>{text}</p>{action}</section>;
}

function Toast({ message }) {
  return <div className={`toast ${message ? "visible" : ""}`} aria-live="polite">{message}</div>;
}

export default function App() {
  return (
    <SiteSettingsProvider>
      <AuthProvider>
        <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/termos" element={<LegalPage type="termos" />} />
        <Route path="/privacidade" element={<LegalPage type="privacidade" />} />
        <Route path="/" element={<Protected><HomePage /></Protected>} />
        <Route path="/temas" element={<Protected><FeatureGate feature="acervo"><ThemesPage /></FeatureGate></Protected>} />
        <Route path="/galeria/:themeId" element={<Protected><FeatureGate feature="acervo"><GalleryPage /></FeatureGate></Protected>} />
        <Route path="/premium" element={<Protected><FeatureGate feature="sombras_molduras"><PremiumPage /></FeatureGate></Protected>} />
        <Route path="/favoritos" element={<Protected><FeatureGate feature="favoritos"><FavoritesPage /></FeatureGate></Protected>} />
        <Route path="/perfil" element={<Protected><ProfilePage /></Protected>} />
        <Route path="/perfil/trocar-senha" element={<Protected><ChangePasswordPage /></Protected>} />
        <Route path="/tutoriais" element={<Protected><FeatureGate feature="tutoriais"><TutorialsPage /></FeatureGate></Protected>} />
        <Route path="/admin" element={<Protected><AdminGate /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </SiteSettingsProvider>
  );
}
